import client from '@/apollo-client';
import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { Banner } from '@/components/basicComponents/banner';
import FileUpload from '@/components/basicComponents/fileUpload';
import { FormFooter } from '@/components/basicComponents/formFooter';
import { ErrorText } from '@/components/basicComponents/input';
import Select, { ISelectValue } from '@/components/basicComponents/select';
import { DropFileArea } from '@/components/dueDiligence-page/dueDiligenceRecord/dropFileArea';
import { isImageExtension } from '@/components/dueDiligence-page/dueDiligenceRecord/utils';
import FileNotSupported from '@/components/dueDiligence-page/modals/fileNotSupported';
import Header from '@/components/header';
import { GoBackButton } from '@/components/header/goBackButton';
import { PageTitle } from '@/components/header/pageTitle';
import { GET_ASSET_SIGNED_URL_QUERY } from '@/components/settings-page/queries';
import { useErrorMessages } from '@/hooks/i18n';
import { useResponsive } from '@/hooks/use-responsive';
import { MainWrap, PaddingWrap } from '@/styles/common';
import { themeVar } from '@/styles/theme';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import styled from 'styled-components';

interface IForm {
  visibility: string;
  file: File | null;
}

const defaultFormValues: IForm = {
  visibility: '',
  file: null
};

const fileExtensions = ['PDF', 'DOC', 'DOCX', 'XLS', 'XLSX', 'CSV', 'JPEG', 'PNG', 'GIF'];

const allowFileExtensions = (files: FileList | null) => {
  return Array.from(files || []).filter((file: File) => {
    return fileExtensions.some((ext) => file.name.toLowerCase().endsWith(ext.toLowerCase()));
  });
};

interface DealDocumentsModalPageProps {
  currentAttachment: {
    assetKey: string;
    url: string;
    visibility: string;
  };
  visibilityOptions: ISelectValue[];
  addNewAttachments: (file: File, visibility: string) => Promise<void>;
  updateAttachmentVisibility: (visibility: string) => Promise<void>;
  handleClosePage: () => void;
}

export const DealDocumentsModalPage = ({
  currentAttachment,
  visibilityOptions,
  addNewAttachments,
  updateAttachmentVisibility,
  handleClosePage
}: DealDocumentsModalPageProps) => {
  const { isMobile } = useResponsive();
  const { default: defaultErrorMessage } = useErrorMessages();
  const { control, setValue, reset, handleSubmit } = useForm<IForm>({ defaultValues: defaultFormValues });

  const [drag, setDrag] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [modalWindow, setModalWindow] = useState({ isOpen: false, type: '' });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!currentAttachment || loading) return;
    reset((defaultValues) => ({ ...defaultValues, visibility: currentAttachment.visibility }));
    setIsEditMode(true);
  }, [currentAttachment]);

  const onDropHandler = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const uploadFiles = e.dataTransfer.files;

    setDrag(false);
    if (!allowFileExtensions(uploadFiles).length) {
      setModalWindow({ isOpen: true, type: 'not-supported' });
      return;
    }
    setValue('file', allowFileExtensions(uploadFiles)[0]);
  };

  const handleUploadFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    const uploadFiles = e.target.files;

    if (!allowFileExtensions(uploadFiles).length) {
      setModalWindow({ isOpen: true, type: 'not-supported' });
      return;
    }
    setValue('file', allowFileExtensions(uploadFiles)[0]);
  };

  const onSubmit = async (data: IForm) => {
    try {
      setLoading(true);
      setErrorMessage(null);

      const { visibility, file } = data;

      if (isEditMode) {
        await updateAttachmentVisibility(visibility);
      } else {
        await addNewAttachments(file, visibility);
      }

      handleClosePage();
    } catch (error) {
      setErrorMessage(defaultErrorMessage);
    } finally {
      setLoading(false);
    }
  };

  const openAttachment = async (assetKey: string) => {
    const { data } = await client.query({
      fetchPolicy: 'network-only',
      query: GET_ASSET_SIGNED_URL_QUERY,
      variables: {
        assetKey: assetKey
      }
    });
    window.open(`${data.getAssetSignedUrl}`, '_blank');
  };

  return (
    <>
      {modalWindow.isOpen && modalWindow.type === 'not-supported' && (
        <FileNotSupported isOpen={modalWindow.isOpen} onClose={() => setModalWindow({ isOpen: false, type: '' })} />
      )}
      <MainWrap>
        <Header modalControl={<GoBackButton handleClose={handleClosePage} backToTitle="Edit Strategy Details" />} />
        <PageTitle title={currentAttachment ? 'Edit Attachment' : 'Add An Attachment'} />
      </MainWrap>
      <MainWrap>
        {errorMessage && (
          <Banner
            icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
            title="Error"
            description={errorMessage}
            bgColor={themeVar('context.error')}
          />
        )}
        <PaddingWrap>
          <DealDocumentsForm onSubmit={handleSubmit(onSubmit, () => window.scrollTo(0, 0))}>
            <DealDocumentsWrapper isMobile={isMobile}>
              <TwoColumnWrapper isMobile={isMobile}>
                <Controller
                  name="visibility"
                  control={control}
                  rules={{
                    required: {
                      value: true,
                      message: 'Visibility is required'
                    }
                  }}
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <Select
                      value={value}
                      setValue={onChange}
                      options={visibilityOptions}
                      errorText={error?.message}
                      label="Visibility"
                      size="md"
                      isRequiredField
                    />
                  )}
                />
              </TwoColumnWrapper>
              {isEditMode && currentAttachment && (
                <FileUpload
                  type={isImageExtension(currentAttachment.assetKey) ? 'image' : 'document'}
                  imageSrc={currentAttachment.url}
                  fileName={currentAttachment.assetKey.split('/').pop()}
                  openFile={() => openAttachment(currentAttachment.assetKey)}
                />
              )}
              {!isEditMode && (
                <Controller
                  name="file"
                  control={control}
                  rules={{
                    required: {
                      value: true,
                      message: 'File is required'
                    }
                  }}
                  render={({ field: { value: file }, fieldState: { error } }) => (
                    <>
                      {file ? (
                        <FileUpload
                          type={file.type.includes('image') ? 'image' : 'document'}
                          imageSrc={URL.createObjectURL(file)}
                          fileName={file.name}
                          openFile={() => {
                            window.open(URL.createObjectURL(file), '_blank');
                          }}
                        />
                      ) : (
                        <div>
                          <DropFileArea
                            id="deal-documents"
                            handleUploadFile={handleUploadFile}
                            onDropHandler={onDropHandler}
                            drag={drag}
                            setDrag={setDrag}
                            maxSize={10}
                            fileExtensions={fileExtensions}
                          />
                          {error?.message && <ErrorText size="md">{error.message}</ErrorText>}
                        </div>
                      )}
                    </>
                  )}
                />
              )}
            </DealDocumentsWrapper>
            <FormFooter onCancel={handleClosePage} disableSaveButton={loading} isLoading={loading} />
          </DealDocumentsForm>
        </PaddingWrap>
      </MainWrap>
    </>
  );
};

const DealDocumentsForm = styled.form`
  margin-top: 50px;
`;

const DealDocumentsWrapper = styled.div<{ isMobile: boolean }>`
  padding: ${({ isMobile }) => (isMobile ? '20px' : '40px')};
  border-radius: 10px;
  border: 1px solid ${themeVar('border.base')};
  background-color: ${themeVar('layer.1')};
  display: flex;
  flex-direction: column;
  gap: ${({ isMobile }) => (isMobile ? '15px' : '25px')};
`;

const TwoColumnWrapper = styled.div<{ isMobile: boolean }>`
  display: grid;
  grid-template-columns: ${({ isMobile }) => (isMobile ? '1fr' : '1fr 1fr')};
  gap: ${({ isMobile }) => (isMobile ? '15px' : '30px')};
`;
